import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Paperclip, ChevronDown, ChevronRight, Search, FileText, Shield, Calculator, Loader2, ClipboardList, Camera, Upload, Trash2, Image, Wand2, Mic, MicOff } from 'lucide-react';
import emailService from '../services/emailService';
import documentLibraryService from '../services/documentLibraryService';
import jobService from '../services/jobService';
import { getFranchiseeIdForDataLoading } from '../App/services/appHelpers';
import { useToast } from './Toast';
import useVoiceInput from '../hooks/useVoiceInput';
import treatmentReportsService from '../services/treatmentReportsService';

const ComposeEmailModal = ({ customer, user, userProfile, jobs = [], onClose, onSent, prefillEmail }) => {
  // Use customer's franchisee_id for email sending, fall back to 'all' for admin
  const franchiseeId = customer?.franchisee_id || getFranchiseeIdForDataLoading(user);
  const loadingFranchiseeId = getFranchiseeIdForDataLoading(user);

  // Form state
  const [toEmail, setToEmail] = useState(prefillEmail || customer?.email || '');
  const [subject, setSubject] = useState('PestControl.co.uk Documents');
  const [messageBody, setMessageBody] = useState(
    `Thank you for using PestControl.co.uk\n\nFurther to our conversation, please find attached the relevant documents.\n\nPlease let me know if you need any further assistance at this time.`
  );

  // Attachment selections
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [selectedGuarantees, setSelectedGuarantees] = useState(new Set());
  const [selectedTreatmentReports, setSelectedTreatmentReports] = useState(new Set());
  const [selectedDocLibrary, setSelectedDocLibrary] = useState(new Set());
  const [uploadedImages, setUploadedImages] = useState([]);

  // Data
  const [customerDocs, setCustomerDocs] = useState({ invoices: [], guarantees: [], treatmentReports: [] });
  const [docLibrary, setDocLibrary] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  // UI state
  const [sending, setSending] = useState(false);
  const [ccSender, setCcSender] = useState(false);
  const [showDocLibraryPopup, setShowDocLibraryPopup] = useState(false);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const toast = useToast();

  const messageRef = useRef(null);
  const voice = useVoiceInput(messageBody, setMessageBody);
  const [polishing, setPolishing] = useState(false);
  const [polishPreview, setPolishPreview] = useState(null);
  const [enhanceHint, setEnhanceHint] = useState(false);

  // Load customer documents and document library
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoadingDocs(true);
    try {
      // Load job documents for all customer jobs
      const allInvoices = [];
      const allGuarantees = [];
      const allTreatmentReports = [];

      if (jobs.length > 0) {
        for (const job of jobs) {
          try {
            const docs = await jobService.getJobDocuments(loadingFranchiseeId, job.id);
            if (docs.invoices) {
              docs.invoices.forEach(inv => {
                if (!allInvoices.find(i => i.id === inv.id)) {
                  allInvoices.push(inv);
                }
              });
            }
            if (docs.guarantees) {
              docs.guarantees.forEach(g => {
                if (!allGuarantees.find(existing => existing.id === g.id)) {
                  allGuarantees.push(g);
                }
              });
            }
            if (docs.treatmentReports) {
              docs.treatmentReports.forEach(tr => {
                if (!allTreatmentReports.find(existing => existing.id === tr.id)) {
                  allTreatmentReports.push(tr);
                }
              });
            }
          } catch (err) {
            console.error(`Error loading docs for job ${job.id}:`, err);
          }
        }
      }

      setCustomerDocs({ invoices: allInvoices, guarantees: allGuarantees, treatmentReports: allTreatmentReports });

      // Load document library
      try {
        const libResult = await documentLibraryService.getDocuments(loadingFranchiseeId);
        setDocLibrary(libResult.documents || []);
      } catch (err) {
        console.error('Error loading document library:', err);
      }
    } catch (error) {
      console.error('Error loading email data:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    if (type === 'error') toast.error(message);
    else toast.success(message);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    files.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImages(prev => [...prev, {
          id: Date.now() + Math.random().toString(36).substr(2, 9),
          data: event.target.result,
          name: file.name,
          type: file.type,
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  const runPolish = async () => {
    if (!messageBody || !messageBody.trim() || polishing) return;
    if (voice.isListening) voice.toggle();
    setPolishing(true);
    try {
      const result = await treatmentReportsService.polishText(messageBody, 'email_message');
      if (result.polished && result.polished !== messageBody) {
        setPolishPreview({ original: messageBody, polished: result.polished });
      }
    } catch (error) {
      console.error('Polish failed:', error);
    } finally {
      setPolishing(false);
    }
  };

  const handleSend = async () => {
    if (!toEmail.trim()) {
      showNotification('Email address is required', 'error');
      return;
    }
    if (!subject.trim()) {
      showNotification('Subject is required', 'error');
      return;
    }

    setSending(true);
    try {
      const result = await emailService.sendEmail(franchiseeId, {
        customer_id: customer?.id || null,
        job_id: jobs.length === 1 ? jobs[0].id : null,
        to_email: toEmail,
        subject,
        message_text: messageBody,
        cc_sender: ccSender,
        invoiceIds: Array.from(selectedInvoices),
        guaranteeIds: Array.from(selectedGuarantees),
        treatmentReportIds: Array.from(selectedTreatmentReports),
        documentLibraryIds: Array.from(selectedDocLibrary),
        imageAttachments: uploadedImages.map(img => ({
          data: img.data,
          name: img.name,
          type: img.type
        }))
      });

      showNotification('Email sent successfully!');
      setTimeout(() => {
        onSent && onSent(result);
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error sending email:', error);
      showNotification(`Failed to send email: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setSending(false);
    }
  };

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGuarantee = (id) => {
    setSelectedGuarantees(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleTreatmentReport = (id) => {
    setSelectedTreatmentReports(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleDocLibrary = (id) => {
    setSelectedDocLibrary(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const totalAttachments = selectedInvoices.size + selectedGuarantees.size + selectedTreatmentReports.size + selectedDocLibrary.size + uploadedImages.length;

  // Group document library by group_name
  const groupedDocs = {};
  const filteredDocs = docLibrary.filter(doc => {
    if (!docSearchQuery) return true;
    const q = docSearchQuery.toLowerCase();
    return (doc.description || '').toLowerCase().includes(q) ||
           (doc.doc_ref || '').toLowerCase().includes(q) ||
           (doc.group_name || '').toLowerCase().includes(q);
  });
  filteredDocs.forEach(doc => {
    const group = doc.group_name || 'General';
    if (!groupedDocs[group]) groupedDocs[group] = [];
    groupedDocs[group].push(doc);
  });

  const formatCurrency = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  // Sender info for signature preview
  const senderName = user?.name || 'PestControl.co.uk';
  const senderPhone = user?.phone || user?.mobile || '';
  const senderEmail = user?.email || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
          <h2 className="text-xl md:text-2xl font-bold text-white truncate">
            Send Email{customer?.name ? ` to ${customer.name}` : ''}
          </h2>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors flex-shrink-0 ml-2">
            <X size={24} />
          </button>
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {/* To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="recipient@example.com"
              required
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Email subject"
              required
            />
          </div>

          {/* Message Body - clickable to open popup */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Message</label>
              <div className="flex items-center gap-1.5">
                {messageBody && messageBody.trim() && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowMessagePopup(true); runPolish(); }}
                    disabled={polishing}
                    title="Enhance"
                    className="p-1.5 rounded-md transition-all bg-gray-100 text-gray-500 hover:bg-purple-500 hover:text-white"
                  >
                    <Wand2 size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowMessagePopup(true); setTimeout(() => { if (!voice.isListening) voice.toggle(); }, 100); }}
                  title="Voice input"
                  className={`relative p-1.5 rounded-md transition-all ${
                    voice.isListening
                      ? 'bg-red-500 text-white shadow-lg shadow-red-300 rounded-full'
                      : 'bg-gray-100 text-gray-500 hover:bg-pest-teal hover:text-white'
                  }`}
                >
                  {voice.isListening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMessagePopup(true)}
              className={`w-full text-left px-4 py-3 border-2 rounded-lg text-sm transition-colors ${
                messageBody
                  ? 'border-gray-300 bg-white text-gray-700 hover:border-blue-500'
                  : 'border-dashed border-gray-300 text-gray-400 hover:border-blue-500 hover:text-gray-600'
              }`}
            >
              {messageBody
                ? <span className="line-clamp-4 whitespace-pre-wrap">{messageBody}</span>
                : '+ Click to compose message...'
              }
              {/* Embedded signature */}
              <span className="block border-t border-gray-200 mt-3 pt-2 text-xs text-gray-500">
                <span className="block text-gray-500 mb-0.5">Best Regards,</span>
                <span className="font-semibold text-gray-700">{senderName}</span>
                {senderPhone && <span className="block">Tel: {senderPhone}</span>}
                {senderEmail && <span className="block">Email: {senderEmail}</span>}
                <span className="block mt-2 text-[9px] text-gray-400 leading-tight">This email and any files transmitted with it are confidential and intended solely for the use of the individual or entity to whom they are addressed.</span>
              </span>
            </button>
          </div>

          {/* CC Sender */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={ccSender}
              onChange={(e) => setCcSender(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Send me a copy (CC)
          </label>

          {/* Image Upload Section */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Image size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Images</span>
              {uploadedImages.length > 0 && (
                <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full">
                  {uploadedImages.length} uploaded
                </span>
              )}
            </div>

            {/* Uploaded image previews */}
            {uploadedImages.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-3">
                {uploadedImages.map((img) => (
                  <div key={img.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200">
                    <img src={img.data} alt={img.name} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={12} />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 px-1 py-0.5">
                      <p className="text-[9px] text-white truncate">{img.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium cursor-pointer hover:from-blue-700 hover:to-blue-800 transition-all">
                <Camera size={16} />
                Take Photo
                <input type="file" accept="image/*" capture="environment" onChange={handleImageUpload} className="hidden" />
              </label>
              <label className="flex items-center gap-2 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-medium cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all">
                <Upload size={16} />
                Upload Images
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </div>

          {/* Document Attachments Section */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Paperclip size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Attachments</span>
              {(selectedInvoices.size + selectedGuarantees.size + selectedTreatmentReports.size + selectedDocLibrary.size) > 0 && (
                <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">
                  {selectedInvoices.size + selectedGuarantees.size + selectedTreatmentReports.size + selectedDocLibrary.size} selected
                </span>
              )}
            </div>

            {loadingDocs ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
                <Loader2 size={16} className="animate-spin" /> Loading documents...
              </div>
            ) : (
              <div className="space-y-1">
                {/* Customer Documents - inline checkboxes */}
                {customerDocs.invoices.map(inv => (
                  <label key={inv.id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer hover:bg-blue-50 px-3 rounded-lg transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.has(inv.id)}
                      onChange={() => toggleInvoice(inv.id)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <FileText size={14} className="text-blue-500 flex-shrink-0" />
                    <span className="truncate">
                      Invoice {inv.invoice_number || inv.id.slice(0,8)}
                      {inv.total != null && <span className="text-gray-400 ml-1">({'\u00A3'}{formatCurrency(inv.total)})</span>}
                    </span>
                  </label>
                ))}

                {customerDocs.guarantees.map(g => (
                  <label key={g.id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer hover:bg-green-50 px-3 rounded-lg transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedGuarantees.has(g.id)}
                      onChange={() => toggleGuarantee(g.id)}
                      className="rounded border-gray-300 text-green-600"
                    />
                    <Shield size={14} className="text-green-500 flex-shrink-0" />
                    <span className="truncate">
                      Guarantee {g.guarantee_number || g.id.slice(0,8)}
                    </span>
                  </label>
                ))}

                {customerDocs.treatmentReports.map(tr => (
                  <div key={tr.id} className="flex items-center gap-2 py-1.5 text-sm hover:bg-indigo-50 px-3 rounded-lg transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedTreatmentReports.has(tr.id)}
                      onChange={() => toggleTreatmentReport(tr.id)}
                      className="rounded border-gray-300 text-indigo-600 cursor-pointer"
                    />
                    <ClipboardList size={14} className="text-indigo-500 flex-shrink-0" />
                    <a href={`/files/treatment-reports/${tr.id}.pdf`} target="_blank" rel="noopener noreferrer" className="truncate text-indigo-600 hover:text-indigo-800 hover:underline">
                      Report {tr.report_number || tr.id.slice(0,8)}
                      {tr.created_at && <span className="text-gray-400 ml-1">Created: {new Date(tr.created_at).toLocaleDateString('en-GB')}</span>}
                      {tr.appointment_date && <span className="text-gray-400 ml-1">| Appt: {new Date(tr.appointment_date).toLocaleDateString('en-GB')}</span>}
                    </a>
                  </div>
                ))}

                {/* Selected doc library items */}
                {Array.from(selectedDocLibrary).map(docId => {
                  const doc = docLibrary.find(d => d.id === docId);
                  if (!doc) return null;
                  const docUrl = doc.filepath ? doc.filepath.replace('/home/pestcont/public_html/members/uploads/', 'https://pestcontrol.co.uk/members/uploads/') : null;
                  return (
                    <div key={docId} className="flex items-center gap-2 py-1.5 text-sm hover:bg-orange-50 px-3 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => toggleDocLibrary(docId)}
                        className="rounded border-gray-300 text-orange-600 cursor-pointer"
                      />
                      <Calculator size={14} className="text-orange-500 flex-shrink-0" />
                      {docUrl ? (
                        <a href={docUrl} target="_blank" rel="noopener noreferrer" className="truncate text-orange-600 hover:text-orange-800 hover:underline">
                          {doc.doc_ref}{doc.description ? ` - ${doc.description}` : ''}
                        </a>
                      ) : (
                        <span className="truncate">{doc.doc_ref}{doc.description ? ` - ${doc.description}` : ''}</span>
                      )}
                    </div>
                  );
                })}

                {/* Document Library button - opens popup */}
                {docLibrary.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowDocLibraryPopup(true)}
                    className="w-full mt-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-sm text-gray-600 hover:text-blue-600 font-medium flex items-center justify-center gap-2"
                  >
                    <Paperclip size={14} />
                    Browse Document Library ({docLibrary.length})
                  </button>
                )}

                {customerDocs.invoices.length === 0 && customerDocs.guarantees.length === 0 && customerDocs.treatmentReports.length === 0 && docLibrary.length === 0 && (
                  <p className="text-sm text-gray-400">No documents available to attach.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 md:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !toEmail.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Email
                {totalAttachments > 0 && (
                  <span className="bg-blue-500 text-xs px-1.5 py-0.5 rounded">
                    {totalAttachments} {totalAttachments === 1 ? 'attachment' : 'attachments'}
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Message Body Popup */}
      {showMessagePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) { if (voice.isListening) voice.toggle(); setShowMessagePopup(false); }}}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal rounded-t-lg">
              <h3 className="text-lg font-semibold text-white">Message</h3>
              <button type="button" onClick={() => { if (voice.isListening) voice.toggle(); setShowMessagePopup(false); }} className="text-white hover:text-gray-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {polishPreview ? (
                <div className="space-y-4">
                  <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                      <span className="text-sm font-semibold text-gray-600">Original</span>
                      <button type="button" onClick={() => { setMessageBody(polishPreview.original); setPolishPreview(null); }} className="text-xs px-3 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium transition-colors">Keep</button>
                    </div>
                    <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{polishPreview.original}</div>
                  </div>
                  <div className="border-2 border-purple-300 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between bg-white px-4 py-2.5 border-b border-purple-200">
                      <span className="text-sm font-semibold text-purple-700 flex items-center gap-1.5"><Wand2 size={14} /> Enhanced</span>
                      <button type="button" onClick={() => { setMessageBody(polishPreview.polished); setPolishPreview(null); }} className="text-xs px-3 py-1.5 rounded-md bg-purple-600 text-white hover:bg-purple-700 font-medium transition-colors">Keep</button>
                    </div>
                    <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{polishPreview.polished}</div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="border-2 border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-pest-blue focus-within:border-pest-blue overflow-hidden">
                    <textarea
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      className="w-full px-4 py-3 text-sm resize-none min-h-[30vh] border-0 focus:ring-0 focus:outline-none"
                      placeholder="Type your message..."
                      autoFocus
                    />
                    {/* Signature - read-only, inside same box */}
                    <div className="px-4 pb-3 border-t border-gray-200 pt-2 bg-gray-50 pointer-events-none select-none">
                      <p className="text-sm text-gray-500 mb-1">Best Regards,</p>
                      <p className="text-sm font-bold text-gray-800">{senderName}</p>
                      {senderPhone && <p className="text-sm text-gray-600">Tel: {senderPhone}</p>}
                      {senderEmail && <p className="text-sm text-gray-600">Email: {senderEmail}</p>}
                      <div className="mt-2">
                        <img src="/logo.png" alt="PestControl.co.uk" className="max-w-[140px] h-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                      <p className="mt-3 text-[10px] text-gray-400 leading-tight">This email and any files transmitted with it are confidential and intended solely for the use of the individual or entity to whom they are addressed. If you are not the intended recipient, please contact the sender and delete all copies. Any views or opinions expressed are solely those of the author and do not necessarily represent those of PestControl.co.uk.</p>
                    </div>
                  </div>
                </>
              )}
            </div>
            {!polishPreview && (
            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  if (!messageBody || !messageBody.trim()) { setEnhanceHint(true); setTimeout(() => setEnhanceHint(false), 2000); return; }
                  runPolish();
                }}
                disabled={polishing}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 font-medium transition-all disabled:opacity-50 inline-flex items-center gap-1.5 relative"
              >
                {polishing ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                {polishing ? 'Enhancing...' : 'Enhance'}
                {enhanceHint && (
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-gray-800 text-white px-2 py-1 rounded">
                    Add text to enhance
                  </span>
                )}
              </button>
              <div className="flex items-center gap-2">
                {voice.isListening ? (
                  <button type="button" onClick={() => voice.toggle()} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all inline-flex items-center gap-1.5">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                    </span>
                    Stop {voice.formatElapsed()}
                  </button>
                ) : (
                  <button type="button" onClick={() => voice.toggle()} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-all inline-flex items-center gap-1.5">
                    <Mic size={15} />
                    Record
                  </button>
                )}
                <button type="button" onClick={() => { if (voice.isListening) voice.toggle(); setShowMessagePopup(false); }} className="px-6 py-2 bg-gradient-to-r from-pest-blue to-pest-teal text-white rounded-lg hover:from-blue-700 hover:to-teal-700 font-medium transition-all">
                  Done
                </button>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Document Library Popup */}
      {showDocLibraryPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-[60] overflow-hidden" onClick={() => setShowDocLibraryPopup(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden m-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Document Library</h2>
                <p className="text-sm text-white/90 mt-1">
                  {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''} available
                  {selectedDocLibrary.size > 0 && ` \u2022 ${selectedDocLibrary.size} selected`}
                </p>
              </div>
              <button
                onClick={() => setShowDocLibraryPopup(false)}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 md:p-6 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={docSearchQuery}
                  onChange={(e) => setDocSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  placeholder="Search documents by name, reference or group..."
                  autoFocus
                />
              </div>
            </div>

            {/* Document List */}
            <div className="p-4 md:p-6 overflow-y-auto flex-1 overscroll-contain">
              {Object.entries(groupedDocs).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(groupedDocs).map(([group, docs]) => (
                    <div key={group}>
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2 px-1">{group}</p>
                      <div className="space-y-1">
                        {docs.map(doc => (
                          <label
                            key={doc.id}
                            className={`flex items-center gap-3 py-3 px-4 rounded-lg cursor-pointer transition-all border-2 ${
                              selectedDocLibrary.has(doc.id)
                                ? 'border-pest-blue bg-blue-50'
                                : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDocLibrary.has(doc.id)}
                              onChange={() => toggleDocLibrary(doc.id)}
                              className="rounded border-gray-300 text-pest-blue focus:ring-pest-blue w-4 h-4"
                            />
                            <FileText size={16} className={selectedDocLibrary.has(doc.id) ? 'text-pest-blue flex-shrink-0' : 'text-orange-500 flex-shrink-0'} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${selectedDocLibrary.has(doc.id) ? 'text-pest-blue' : 'text-gray-800'}`}>
                                {doc.description}
                              </p>
                              {doc.doc_ref && (
                                <p className="text-xs text-gray-400">{doc.doc_ref}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : docSearchQuery ? (
                <p className="text-center text-gray-400 py-8">No documents match your search.</p>
              ) : (
                <p className="text-center text-gray-400 py-8">No documents available.</p>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {selectedDocLibrary.size} document{selectedDocLibrary.size !== 1 ? 's' : ''} selected
              </p>
              <button
                type="button"
                onClick={() => setShowDocLibraryPopup(false)}
                className="px-6 py-2.5 bg-gradient-to-r from-pest-blue to-pest-teal text-white rounded-lg hover:from-blue-700 hover:to-teal-700 font-medium transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposeEmailModal;
